import { Input, Component, forwardRef, OnInit } from '@angular/core';
import { NG_VALIDATORS } from '@angular/forms';

import { IMediaFeatures } from 'rev-shared/media/IMediaFeatures';
import { FileWrapper } from 'rev-shared/ui/fileUpload/FileWrapper';
import { FileUtil } from 'rev-shared/util/FileUtil';
import { toggleError } from 'rev-shared/util/directives/form/validation/validationUtil';
import { IImageComponentMetadata, IImageData } from 'rev-shared/ui/image-selector/ImageSelector.Contract';
import { EditWebcastBaseSettingsComponent } from './EditWebcastBaseSettings.Component';
import { EditWebcastModel } from './EditWebcastModel';
import { EditWebcastService } from './EditWebcast.Service';
import { POLL_ANSWERS_DEFAULT } from '../polls/Polls';
import { PresentationFileStatus } from '../presentations/PresentationFileStatus';
import { WebcastQuestionOption, UserPollsResponseTypes } from '../webcast/WebcastStatus';
import { WebcastVideoSource } from '../webcast/WebcastVideoSource';

import 'rev-less/video/video-main.less'; //dependy for thumbnail-picker-overlay css class. Refactor later.
import styles from './EditWebcast.module.less';

@Component({
	selector: 'edit-webcast-engagement-settings',
	templateUrl: './EditWebcastEngagementSettings.Component.html',
	providers: [{
		provide: NG_VALIDATORS,
		useExisting: forwardRef(() => EditWebcastEngagementSettingsComponent),
		multi: true,
	}],
	standalone: false
})
export class EditWebcastEngagementSettingsComponent extends EditWebcastBaseSettingsComponent implements OnInit {
	@Input() public webcast: EditWebcastModel;
	@Input() public features: IMediaFeatures;
	@Input() public isSidebar: boolean;

	public readonly WebcastQuestionOptionTypes = WebcastQuestionOption;
	public readonly UserPollsResponseTypes = UserPollsResponseTypes;
	public readonly PresentationFileExtensions = FileUtil.presentationFileExtensions;
	public readonly styles = styles;
	public readonly UploadFailed = PresentationFileStatus.UploadingFailed;

	public previewThumbnailImageComponentMetadata: IImageComponentMetadata = {
		requiredValidation: false,
		showResetButton: true,
		readOnly: false
	};

	public backgroundImageComponentMetadata: IImageComponentMetadata = {
		requiredValidation: false,
		showResetButton: true,
		readOnly: false
	};

	public previewThumbnailImageData: IImageData;

	public backgroundImageData: IImageData;

	public bgStatus: any = { active: true };

	public model: any;

	public ngOnInit(): void {
		this.previewThumbnailImageData = { url: this.webcast.previewThumbnailUri };
		this.backgroundImageData = { url: this.webcast.thumbnailUri };
	}

	public addPoll(): void {
		this.webcast.polls = this.webcast.polls.concat([{
			question: '',
			answers: Array.from({ length: POLL_ANSWERS_DEFAULT }).map(() => ({ text: '' })),
			multipleChoice: false
		}]);
	}

	public get isAddPollVisible(): boolean {
		return !this.isSidebar && this.accountLevelPollsEnabled
			&& this.webcast && this.webcast.pollsEnabled;
	}

	public get showPresentationFile(): boolean {
		return this.webcast.videoSource !== WebcastVideoSource.VOD &&
			this.webcast.videoSource !== WebcastVideoSource.WEBRTC_SINGLE_PRESENTER &&
			this.webcast.videoSource !== WebcastVideoSource.PRODUCER;
	}

	public get accountLevelEmbeddedContentEnabled(): boolean {
		return this.features?.webcastEmbedContentSettings?.isAvailable && this.features?.webcastEmbedContentSettings?.isEnabled;
	}

	public get accountLevelPollsEnabled(): boolean {
		return this.features?.webcastEngagementSettings?.allowPolls;
	}

	public setPresentationFile(file: FileWrapper): void {
		this.webcast.presentationFile = file;
		this.webcast.presentation.name = file && file.name;
		this.webcast.presentation.status = null;

		const control = this.form.control.get('presentationFileUpload');
		control.markAsDirty();
		toggleError(control, 'fileType', file && !FileUtil.isPresentationFile(file.extension));
	}

	public removePresentationFile(): void {
		this.setPresentationFile(undefined);
	}

	public removePoll(index: number): void {
		this.webcast.polls.splice(index, 1);
	}

	public onTogglePolls(isEnabled: boolean) : void {
		if (isEnabled && !this.webcast.userPollsResponse) {
			this.webcast.userPollsResponse = UserPollsResponseTypes.ANONYMOUS;
		}
	}
	public onPreviewThumbnailSelect(): void {
		Object.assign(this.webcast, {
			previewThumbnailFile: this.previewThumbnailImageData?.fileWrapper,
			previewThumbnailImageId: this.previewThumbnailImageData ? this.webcast.previewThumbnailImageId : null,
			previewThumbnailUri: this.previewThumbnailImageData ? this.previewThumbnailImageData.url : null
		});
	}
	public onBackgroundSelect(): void {
		Object.assign(this.webcast, {
			backgroundImageFile: this.backgroundImageData?.fileWrapper,
			imageId: this.backgroundImageData ? this.webcast.imageId : null,
			thumbnailUri: this.backgroundImageData ? this.backgroundImageData.url : null
		});
	}
}
